import { useState, useEffect, useRef } from "react";
import api from "./api/axios";
import useAuth from "./hooks/useAuth";

import {
  FiUpload,
  FiImage,
  FiClock,
  FiCheckCircle,
  FiX,
  FiTrash2,
  FiSend,
  FiMessageCircle,
} from "react-icons/fi";

import "./memo.css";


const MAX_FILES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_MESSAGE_LENGTH = 500;

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

export default function StudentMemories() {
  // =====================================================
  // STATE
  // =====================================================

  const [activeTab, setActiveTab] = useState("pending");

  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
const [deletingSubmissionId, setDeletingSubmissionId] =
  useState(null);
  const [uploads, setUploads] = useState([]);
const [photoViewer, setPhotoViewer] = useState(null);
  const [uploading, setUploading] = useState(false);
const [loadingMemories, setLoadingMemories] = useState(false);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [submissionToDelete, setSubmissionToDelete] = useState(null);
const fileInputRef = useRef(null);
const [successMessage, setSuccessMessage] = useState("");
// =====================================================
// GET LOGGED-IN USER FROM AUTH
// =====================================================
const { user } = useAuth();

const userRole = String(user?.role || "").trim().toLowerCase();

const isFaculty = userRole === "faculty";
const isStudent = userRole === "student";

console.log("========== MEMORY ROLE DEBUG ==========");
console.log("USER FROM useAuth:", user);
console.log("ROLE FROM USER:", user?.role);
console.log("NORMALIZED ROLE:", userRole);
console.log("IS STUDENT:", isStudent);
console.log("IS FACULTY:", isFaculty);
console.log("========================================");  // =====================================================
  // FETCH MEMORIES
  // =====================================================

  useEffect(() => {
    fetchMemories();
  }, []);

  const fetchMemories = async () => {
    try {
      setLoadingMemories(true);

const response = await api.get("/api/memories");

      const memories = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.memories)
        ? response.data.memories
        : [];

      setUploads(memories);
    } catch (error) {
      console.error("❌ FETCH MEMORIES ERROR:", error);

      setUploads([]);

      if (error.response?.status === 401) {
        console.warn("⚠️ User is not authenticated.");
      }
    } finally {
      setLoadingMemories(false);
    }
  };



const handleDeleteSubmission = async () => {
  if (!submissionToDelete) return;

  const item = submissionToDelete;

  if (item.status !== "pending") {
    setShowDeleteModal(false);
    return;
  }

  const submissionKey = item.submission_id || item.id;

  try {
    setDeletingSubmissionId(submissionKey);
    setSuccessMessage("");

    await api.delete(`/api/memories/${item.id}`);

    setShowDeleteModal(false);
    setSubmissionToDelete(null);

    setSuccessMessage(
      isFaculty
        ? "Your pending faculty message has been deleted successfully."
        : "Your pending memory submission has been deleted successfully."
    );

    await fetchMemories();
  } catch (error) {
    console.error("DELETE MEMORY SUBMISSION ERROR:", error);

    setShowDeleteModal(false);

    setSuccessMessage(
      error.response?.data?.message ||
        "Unable to delete this submission. Please try again."
    );
  } finally {
    setDeletingSubmissionId(null);
  }
};


  // =====================================================
  // CLEAN UP PREVIEW URLS
  // =====================================================

  useEffect(() => {
    return () => {
      files.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [files]);

  // =====================================================
  // FILE SELECTION
  // STUDENT ONLY
  // =====================================================

const handleFileChange = (event) => {
  const selectedFiles = Array.from(event.target.files || []);

  if (selectedFiles.length === 0) {
    return;
  }
    // ---------------------------------------------------
    // MAX FILE COUNT
    // ---------------------------------------------------

    if (files.length + selectedFiles.length > MAX_FILES) {
      alert(
        `You can upload a maximum of ${MAX_FILES} images at once.`
      );

      event.target.value = "";
      return;
    }

    // ---------------------------------------------------
    // FILE TYPE VALIDATION
    // ---------------------------------------------------

    const invalidFiles = selectedFiles.filter(
      (file) => !ALLOWED_TYPES.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      alert(
        "Only JPG, JPEG, PNG, and WEBP images are allowed."
      );

      event.target.value = "";
      return;
    }

    // ---------------------------------------------------
    // FILE SIZE VALIDATION
    // ---------------------------------------------------

    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > MAX_FILE_SIZE
    );

    if (oversizedFiles.length > 0) {
      alert(
        "Each image must be smaller than 10MB."
      );

      event.target.value = "";
      return;
    }

    // ---------------------------------------------------
    // PREVENT DUPLICATES
    // ---------------------------------------------------

    const existingKeys = new Set(
      files.map(
        (item) =>
          `${item.file.name}-${item.file.size}-${item.file.lastModified}`
      )
    );

    const newFiles = selectedFiles.filter((file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;

      return !existingKeys.has(key);
    });

    if (newFiles.length === 0) {
      event.target.value = "";
      return;
    }

    // ---------------------------------------------------
    // CREATE PREVIEW URL
    // ---------------------------------------------------

    const filesWithPreview = newFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setFiles((previousFiles) => [
      ...previousFiles,
      ...filesWithPreview,
    ]);

    // Allow selecting the same file again later
    event.target.value = "";
  };

  // =====================================================
  // REMOVE ONE FILE
  // =====================================================

  const removeFile = (indexToRemove) => {
    setFiles((previousFiles) => {
      const fileToRemove =
        previousFiles[indexToRemove];

      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(
          fileToRemove.previewUrl
        );
      }

      return previousFiles.filter(
        (_, index) => index !== indexToRemove
      );
    });
  };

  // =====================================================
  // CLEAR ALL FILES
  // =====================================================

  const clearFiles = () => {
    files.forEach((item) => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });

    setFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =====================================================
  // OPEN FILE PICKER
  // STUDENT ONLY
  // =====================================================

  const openFilePicker = () => {
    if (!isStudent || uploading) {
      return;
    }

    fileInputRef.current?.click();
  };

  // =====================================================
  // SUBMIT MEMORY / FACULTY MESSAGE
  // =====================================================

  const handleUpload = async () => {
    // ---------------------------------------------------
    // VALIDATE MESSAGE
    // ---------------------------------------------------

    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      alert(
        isFaculty
          ? "Please write your faculty message."
          : "Please write a message."
      );

      return;
    }


    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      alert(
        `Your message must not exceed ${MAX_MESSAGE_LENGTH} characters.`
      );

      return;
    }

    // ---------------------------------------------------
    // STUDENT MUST HAVE IMAGE
    // FACULTY DOES NOT NEED IMAGE
    // ---------------------------------------------------

    if (isStudent && files.length === 0) {
      alert("Please select at least one image.");
      return;
    }

    if (uploading) {
      return;
    }

    // ---------------------------------------------------
    // FORM DATA
    // ---------------------------------------------------

    const formData = new FormData();

    /*
      STUDENT:
      images + message

      FACULTY:
      message ONLY
    */

    if (isStudent) {
      files.forEach((item) => {
        formData.append("images", item.file);
      });
    }

    formData.append("message", trimmedMessage);

    try {
      setUploading(true);

      console.log(
        "=========================================="
      );

      if (isFaculty) {
        console.log(
          "💬 SUBMITTING FACULTY MESSAGE"
        );
        console.log("🖼️ IMAGES: NOT ALLOWED");
      } else {
        console.log(
          "📸 UPLOADING STUDENT MEMORIES"
        );
        console.log(
          "🖼️ NUMBER OF IMAGES:",
          files.length
        );
      }

      console.log("💬 MESSAGE:", trimmedMessage);

      console.log(
        "=========================================="
      );

const response = await api.post(
  "/api/memories",
  formData
);

      console.log(
        "✅ SUBMISSION SUCCESS:",
        response.data
      );

          setSuccessMessage(
  isFaculty
    ? "Your faculty message has been submitted successfully and is now waiting for administrator approval."
    : "Your memories have been uploaded successfully!"
);

      // ---------------------------------------------------
      // CLEAR FORM
      // ---------------------------------------------------

      clearFiles();
      setMessage("");

      // ---------------------------------------------------
      // REFRESH
      // ---------------------------------------------------

      await fetchMemories();

      setActiveTab("pending");
    } catch (error) {
      console.error(
        "❌ MEMORY SUBMISSION ERROR:",
        error
      );

      const status = error.response?.status;

      const serverMessage =
        error.response?.data?.message;

      if (status === 401) {
        alert(
          "Your session has expired. Please log in again."
        );

        return;
      }

      if (status === 403) {
        alert(
          serverMessage ||
            "Your account is not allowed to submit memories."
        );

        return;
      }

      if (status === 413) {
        alert(
          serverMessage ||
            "One or more images are too large. Each image must be smaller than 10MB."
        );

        return;
      }

      if (status === 400) {
        alert(
          serverMessage ||
            "Please check your message and selected images."
        );

        return;
      }

      if (status >= 500) {
        alert(
          "The server could not save your submission. Please try again."
        );

        return;
      }

      alert(
        serverMessage ||
          "Failed to submit. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  // =====================================================
  // FILTER MEMORIES
  // =====================================================

  // =====================================================
// GROUP MEMORIES BY SUBMISSION
// ONE SUBMISSION = ONE FOLDER / CARD
// =====================================================

const groupedUploads = Object.values(
  uploads.reduce((groups, item) => {
    const key =
      item.submission_id || `single-${item.id}`;

    if (!groups[key]) {
      groups[key] = {
        ...item,
        images: [],
        items: [],
      };
    }

    // Store every database row belonging to this submission
    groups[key].items.push(item);

    // Store images in the same folder
    if (item.image) {
      groups[key].images.push(item.image);
    }

    return groups;
  }, {})
);

// =====================================================
// DETERMINE SUBMISSION STATUS
// ONE FOLDER HAS ONE STATUS
// =====================================================


const submissions = groupedUploads.map((submission) => {
  const statuses = submission.items.map((item) =>
    String(item.status || "pending").toLowerCase()
  );

  let submissionStatus = "pending";

  if (statuses.length > 0 && statuses.every((s) => s === "approved")) {
    submissionStatus = "approved";
  } else if (statuses.some((s) => s === "rejected")) {
    submissionStatus = "rejected";
  }

  return {
    ...submission,
    status: submissionStatus,
  };
});


// =====================================================
// FILTER SUBMISSIONS
// FILTER AFTER GROUPING
// =====================================================

const filteredUploads = submissions.filter(
  (submission) =>
    submission.status === activeTab
);

// =====================================================
// COUNTS
// ONE FOLDER = ONE COUNT
// =====================================================

const pendingCount = submissions.filter(
  (submission) =>
    submission.status === "pending"
).length;

const approvedCount = submissions.filter(
  (submission) =>
    submission.status === "approved"
).length;

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    );
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="memories-page">

      {/* =================================================
          HEADER
      ================================================= */}
{successMessage && (
  <div className="success-notification" role="status">
    <FiCheckCircle />
    <span>{successMessage}</span>
    <button
      type="button"
      onClick={() => setSuccessMessage("")}
      aria-label="Close notification"
    >
      <FiX />
    </button>
  </div>
)}
      <div className="memories-header">
        <div>
          <span className="memories-label">
            ADSSU YEARBOOK
          </span>

          <h1>
            {isFaculty
              ? "Faculty Message"
              : "Memories"}
          </h1>

         <p>
  {isFaculty
    ? "Share a meaningful message with the graduating students."
    : "Share your favorite memories from 1st year to 4th year and celebrate your college journey."}
</p>
        </div>
      </div>

      {/* =================================================
          SUBMISSION CARD
      ================================================= */}

      <div className="upload-card-box">

        {/* HEADER */}

        <div className="upload-card-header">

          <div className="upload-icon">
            {isFaculty ? (
              <FiMessageCircle />
            ) : (
              <FiUpload />
            )}
          </div>

          <div>
            <h2>
              {isFaculty
                ? "Share Your Message"
                : "Share Your Memories"}
            </h2>

            <p>
              {isFaculty
                ? "Write a meaningful message for the yearbook."
                : "Share photos and memories from your journey from 1st year to 4th year."}
            </p>
          </div>

        </div>

        {/* =================================================
            STUDENT IMAGE UPLOAD ONLY
        ================================================= */}

        {isStudent && (
          <>
            {/* FILE UPLOAD AREA */}

            <div className="memory-upload-area">

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                onChange={handleFileChange}
                disabled={uploading}
                className="memory-file-input"
              />

              <div
                className="upload-drop-content"
                onClick={openFilePicker}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === " "
                  ) {
                    openFilePicker();
                  }
                }}
              >

                <div className="large-upload-icon">
                  <FiImage />
                </div>

<h3>
  Share Your College Memories
</h3>

<p>
  Choose photos from your 1st year to 4th year
</p>

               <span>
  JPG, JPEG, PNG or WEBP. Max 10MB each . Up to 20 images
</span>

                <button
                  type="button"
                  className="choose-images-btn"
                  disabled={uploading}
                  onClick={(event) => {
                    event.stopPropagation();
                    openFilePicker();
                  }}
                >
                  <FiImage />
                  Choose Images
                </button>

              </div>
            </div>

            {/* =================================================
                SELECTED IMAGES
            ================================================= */}

            {files.length > 0 && (
              <div className="selected-images-section">

                <div className="selected-images-header">

                  <div>
                    <h3>
                      Selected Photos
                    </h3>

                    <span>
                      {files.length}{" "}
                      {files.length === 1
                        ? "image"
                        : "images"}{" "}
                      selected
                    </span>
                  </div>

                  <button
                    type="button"
                    className="clear-images-btn"
                    onClick={clearFiles}
                    disabled={uploading}
                  >
                    <FiTrash2 />
                    Clear All
                  </button>

                </div>

                <div className="selected-images-grid">

                  {files.map((item, index) => (
                    <div
                      className="selected-image-item"
                      key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}
                    >

                      <img
                        src={item.previewUrl}
                        alt={`Selected memory ${
                          index + 1
                        }`}
                      />

                      <div className="image-number">
                        {index + 1}
                      </div>

                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() =>
                          removeFile(index)
                        }
                        disabled={uploading}
                        aria-label={`Remove ${item.file.name}`}
                      >
                        <FiX />
                      </button>

                      <div className="image-file-name">
                        {item.file.name}
                      </div>

                    </div>
                  ))}

                </div>
              </div>
            )}
          </>
        )}

        {/* =================================================
            MESSAGE
        ================================================= */}

        <div className="memory-message-section">

          <div className="message-label-row">

            <label htmlFor="memory-message">

              <FiMessageCircle />

              {isFaculty
                ? "Faculty Message"
                : "Memory Message"}

            </label>

            <span
              className={
                message.length >
                MAX_MESSAGE_LENGTH
                  ? "character-limit exceeded"
                  : "character-limit"
              }
            >
              {message.length}/
              {MAX_MESSAGE_LENGTH}
            </span>

          </div>

          <textarea
            id="memory-message"
            className="memory-message-input"
            placeholder={
              isFaculty
                ? "Write a meaningful message for the graduating students..."
                : "Write something meaningful about these memories..."
            }
            value={message}
            maxLength={MAX_MESSAGE_LENGTH}
            onChange={(event) =>
              setMessage(event.target.value)
            }
            disabled={uploading}
            rows={5}
          />

          <p className="message-helper">
            {isFaculty
              ? "Your message will be reviewed by the yearbook administrator before publication."
              : "This message will be attached to all selected photos."}
          </p>

        </div>

        {/* =================================================
            SUBMIT BUTTON
        ================================================= */}

        <button
          type="button"
          className={`upload-memory-btn ${
            uploading ? "uploading" : ""
          }`}
          onClick={handleUpload}
          disabled={
            uploading ||
            !message.trim() ||
            (isStudent && files.length === 0)
          }
        >

          {uploading ? (
            <>
              <span className="upload-spinner"></span>

              {isFaculty
                ? "Sending..."
                : `Uploading ${files.length} ${
                    files.length === 1
                      ? "memory"
                      : "memories"
                  }...`}
            </>
          ) : (
            <>
              <FiSend />

              {isFaculty
                ? "Send Faculty Message"
                : `Upload ${
                    files.length > 0
                      ? `${files.length} ${
                          files.length === 1
                            ? "Memory"
                            : "Memories"
                        }`
                      : "Memory"
                  }`}
            </>
          )}

        </button>

      </div>

      {/* =================================================
          MY SUBMISSIONS
      ================================================= */}

      <div className="memories-section">

        <div className="memories-section-header">

          <div>

            <h2>
              {isFaculty
                ? "My Messages"
                : "My Memories"}
            </h2>

            <p>
              {isFaculty
                ? "View the messages you submitted for the yearbook."
                : "View the memories you submitted for the yearbook."}
            </p>

          </div>

        </div>

        {/* =================================================
            TABS
        ================================================= */}

        <div className="tab-switch">

          <button
            type="button"
            className={
              activeTab === "pending"
                ? "active"
                : ""
            }
            onClick={() =>
              setActiveTab("pending")
            }
          >
            <FiClock />

            <span>
              Pending
            </span>

            <strong>
              {pendingCount}
            </strong>
          </button>

          <button
            type="button"
            className={
              activeTab === "approved"
                ? "active"
                : ""
            }
            onClick={() =>
              setActiveTab("approved")
            }
          >
            <FiCheckCircle />

            <span>
              Approved
            </span>

            <strong>
              {approvedCount}
            </strong>
          </button>

                    <button
            type="button"
            className={activeTab === "rejected" ? "active" : ""}
            onClick={() => setActiveTab("rejected")}
          >
            <FiX />
            <span>Rejected</span>
            <strong>
              {submissions.filter(
                (submission) => submission.status === "rejected"
              ).length}
            </strong>
          </button>

        </div>

        {/* =================================================
            SUBMISSIONS GRID
        ================================================= */}

        <div className="upload-grid">

          {loadingMemories ? (
            <div className="memories-loading">

              <div className="memory-loading-spinner"></div>

              <p>
                Loading your submissions...
              </p>

            </div>
          ) : filteredUploads.length === 0 ? (
            <div className="memories-empty">

              <div className="empty-memory-icon">
                {isFaculty ? (
                  <FiMessageCircle />
                ) : (
                  <FiImage />
                )}
              </div>

              <h3>
                No {activeTab}{" "}
                {isFaculty
                  ? "messages"
                  : "memories"}
              </h3>

              <p>
                {activeTab === "pending"
                  ? isFaculty
                    ? "Your submitted messages will appear here while waiting for approval."
                    : "Your submitted memories will appear here while waiting for approval."
                  : isFaculty
                  ? "Approved messages will appear here once they are reviewed."
                  : "Approved memories will appear here once they are reviewed."}
              </p>

            </div>
          ) : (
            filteredUploads.map((item) => (
             <div
  className={`memory-card ${
    isFaculty ? "faculty-submission-card" : "student-submission-card"
  }`}
  key={item.submission_id || item.id}
>

                {/* =================================================
                    IMAGE
                    ONLY SHOWN IF DATABASE HAS IMAGE
                ================================================= */}

{/* =================================================*
    IMAGE / GALLERY
    ONE SUBMISSION = ONE CARD
================================================= */}

{item.images.length > 0 ? (
  <div className="memory-card-gallery">


{item.images.slice(0, 4).map((image, index) => (
  <div
    className="memory-gallery-image"
    key={`${image}-${index}`}
  >
    <img
      src={image}
      alt={`Memory ${index + 1}`}
      onClick={() =>
        setPhotoViewer({
          images: item.images,
          index,
        })
      }
      style={{ cursor: "zoom-in" }}
    />

    {index === 3 && item.images.length > 4 && (
      <div
        className="memory-more-overlay"
        onClick={() =>
          setPhotoViewer({
            images: item.images,
            index: 3,
          })
        }
      >
        +{item.images.length - 4}
      </div>
    )}
  </div>
))}


    <span
      className={`memory-status ${item.status}`}
    >
{item.status === "approved" ? (
  <>
    <FiCheckCircle />
    Approved
  </>
) : item.status === "rejected" ? (
  <>
    <FiX />
    Rejected
  </>
) : (
  <>
    <FiClock />
    Pending
  </>
)}
    </span>

  </div>
) : (
  /* =================================================
     FACULTY MESSAGE CARD
  ================================================= */

  <div className="memory-card-no-image">

    

    <span
      className={`memory-status ${item.status}`}
    >
      {item.status === "approved" ? (
        <>
          <FiCheckCircle />
          Approved
        </>
      ) : (
        <>
          <FiClock />
          Pending
        </>
      )}
    </span>

  </div>
)}

                {/* =================================================
                    CONTENT
                ================================================= */}

  
<div className="memory-content">
  {isFaculty ? (
    <div className="faculty-submitted-letter">
      <div className="faculty-submitted-letterhead">
        <div className="faculty-submitted-seal">ADSSU</div>

        <div>
          <h3>AGUSAN DEL SUR STATE UNIVERSITY</h3>
          <p>DIGITAL YEARBOOK</p>
        </div>
      </div>

      <div className="faculty-submitted-divider" />

      <div className="faculty-submitted-meta">
        <span>FACULTY MESSAGE</span>
        <span>
          Submitted {formatDate(item.created_at)}
        </span>
      </div>

      <div className="faculty-submitted-recipient">
        <small>TO</small>
        <strong>The Graduating Students</strong>
        <span>For the Yearbook</span>
      </div>

      <p className="faculty-submitted-salutation">
        Dear Graduating Students,
      </p>

      <p className="faculty-submitted-message">
        {item.message}
      </p>

      <div className="faculty-submitted-signature">
        <span>With my warmest regards,</span>
        <strong>{item.full_name || "Faculty Member"}</strong>
        <small>{item.department_program || "Faculty"}</small>
      </div>

      <div
        className={`faculty-submitted-status ${
          item.status === "approved" ? "approved" : "pending"
        }`}
      >
        {item.status === "approved" ? (
          <FiCheckCircle />
        ) : (
          <FiClock />
        )}

        <div>
          <strong>
            {item.status === "approved"
              ? "Approved"
              : "Pending Approval"}
          </strong>
          <span>
            {item.status === "approved"
              ? "Your message has been approved for the yearbook."
              : "Your message is awaiting review by the yearbook administrator."}
          </span>
        </div>
      </div>
    </div>
  ) : (
    <>
      <div className="memory-user">
        {item.profile_pic ? (
          <img
            src={item.profile_pic}
            alt={item.full_name || "User"}
          />
        ) : (
          <div className="memory-avatar">
            {(item.full_name || "U").charAt(0).toUpperCase()}
          </div>
        )}

        <div>
          <h4>{item.full_name || "You"}</h4>
          <span>{formatDate(item.created_at)}</span>
        </div>
      </div>

      {item.message && (
        <p className="memory-message">{item.message}</p>
      )}
    </>
  )}

  {item.status === "pending" && (
  <div className="memory-card-actions">
    <button
      type="button"
      className="delete-submission-btn"
      onClick={() => {
  setSubmissionToDelete(item);
  setShowDeleteModal(true);
}}nClick={() => handleDeleteSubmission(item)}
      disabled={
        deletingSubmissionId ===
        (item.submission_id || item.id)
      }
      aria-label="Delete pending submission"
    >
      <FiTrash2 />
      {deletingSubmissionId ===
      (item.submission_id || item.id)
        ? "Deleting..."
        : "Delete Submission"}
    </button>
  </div>
)}
</div>


              </div>
            ))
          )}

        </div>

      </div>

      {showDeleteModal && submissionToDelete && (
  <div className="delete-modal-overlay">
    <div
      className="delete-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
    >
      <div className="delete-modal-icon">
        <FiTrash2 />
      </div>

      <h2 id="delete-modal-title">Delete Submission?</h2>

      <p>
        {isFaculty
          ? "Are you sure you want to delete your pending faculty message?"
          : "Are you sure you want to delete this pending memory submission and all its photos?"}
      </p>

      <p className="delete-modal-warning">
        This action cannot be undone.
      </p>

      <div className="delete-modal-actions">
        <button
          type="button"
          className="delete-modal-cancel"
          onClick={() => {
            setShowDeleteModal(false);
            setSubmissionToDelete(null);
          }}
          disabled={deletingSubmissionId !== null}
        >
          Cancel
        </button>

        <button
          type="button"
          className="delete-modal-confirm"
          onClick={handleDeleteSubmission}
          disabled={deletingSubmissionId !== null}
        >
          {deletingSubmissionId !== null
            ? "Deleting..."
            : "Yes, Delete"}
        </button>
      </div>
    </div>
  </div>
)}


{photoViewer && (
  <div
    className="photo-viewer-overlay"
    onClick={() => setPhotoViewer(null)}
  >
    <div
      className="photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="View memory photos"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="photo-viewer-close"
        onClick={() => setPhotoViewer(null)}
        aria-label="Close photo viewer"
      >
        <FiX />
      </button>

      <button
        type="button"
        className="photo-viewer-nav previous"
        onClick={() =>
          setPhotoViewer((current) => ({
            ...current,
            index:
              (current.index - 1 + current.images.length) %
              current.images.length,
          }))
        }
        aria-label="Previous photo"
      >
        ‹
      </button>

      <img
        className="photo-viewer-image"
        src={photoViewer.images[photoViewer.index]}
        alt={`Memory photo ${photoViewer.index + 1}`}
      />

      <button
        type="button"
        className="photo-viewer-nav next"
        onClick={() =>
          setPhotoViewer((current) => ({
            ...current,
            index:
              (current.index + 1) % current.images.length,
          }))
        }
        aria-label="Next photo"
      >
        ›
      </button>

      <div className="photo-viewer-counter">
        Photo {photoViewer.index + 1} of {photoViewer.images.length}
      </div>
    </div>
  </div>
)}


    </div>
  );
}
